import { useQuery } from "@tanstack/react-query";
import { ArrowDown, ArrowUp } from "lucide-react";
import { useTranslations } from "use-intl";

import { Card, CardContent, CardHeader, CardTitle } from "@lawline/ui/components/card";
import { Skeleton } from "@lawline/ui/components/skeleton";

import { formatMoney, simpleDateFmt } from "@platform/fmt";
import { billingCostQueryOptions, billingCostSeriesQueryOptions } from "../billing.queries";
import { BillingSpendChart } from "./billing-spend-graph";

// Percentage change vs the previous period, green when down, red when up.
// Renders nothing without a previous amount or when it was zero.
function CostDelta({ amount, previous }: { amount: number; previous: number | null }) {
	if (previous == null || previous === 0) return null;
	const pct = ((amount - previous) / previous) * 100;
	const up = pct > 0;
	const Icon = up ? ArrowUp : ArrowDown;
	return (
		<span
			className={`flex items-center gap-0.5 text-sm font-medium ${
				up ? "text-destructive" : "text-green-600 dark:text-green-500"
			}`}
		>
			<Icon className="size-3.5" />
			{Math.abs(pct).toFixed(1)}%
		</span>
	);
}

export function BillingSpend() {
	const t = useTranslations("billing.spend");
	const cost = useQuery(billingCostQueryOptions());
	const spend = useQuery(billingCostSeriesQueryOptions());

	return (
		<div className="flex flex-col gap-8">
			<Card>
				<CardHeader>
					<CardTitle className="text-muted-foreground text-sm font-medium">
						{t("thisPeriod")}
					</CardTitle>
				</CardHeader>
				<CardContent>
					{cost.isPending ? (
						<Skeleton className="h-8 w-32" />
					) : cost.error ? (
						<p className="text-destructive text-sm">{t("loadError")}</p>
					) : (
						<>
							<div className="flex items-baseline gap-2">
								<p className="text-3xl font-semibold">
									{formatMoney(cost.data.amount, cost.data.currency)}
								</p>
								<CostDelta amount={cost.data.amount} previous={cost.data.previousAmount} />
							</div>
							<p className="text-muted-foreground text-xs">
								{cost.data.periodEnd &&
									t("periodRange", {
										periodStart: simpleDateFmt.format(new Date(cost.data.periodStart)),
										periodEnd: simpleDateFmt.format(new Date(cost.data.periodEnd)),
									})}
							</p>
						</>
					)}
				</CardContent>
			</Card>

			<Card>
				<CardHeader>
					<CardTitle className="text-base">{t("overTime")}</CardTitle>
				</CardHeader>
				<CardContent>
					{spend.isPending ? (
						<Skeleton className="h-[320px] w-full" />
					) : spend.error ? (
						<p className="text-destructive flex h-[320px] items-center justify-center text-sm">
							{t("loadDataError")}
						</p>
					) : spend.data.points.length === 0 ? (
						<p className="text-muted-foreground flex h-[320px] items-center justify-center text-sm">
							{t("empty")}
						</p>
					) : (
						<BillingSpendChart points={spend.data.points} currency={spend.data.currency} />
					)}
				</CardContent>
			</Card>
		</div>
	);
}
